import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

type Version = Database['public']['Tables']['versions']['Row'];
type ChangelogEntry = Database['public']['Tables']['changelog_entries']['Row'];

interface VersionWithEntries extends Version {
  changelog_entries: ChangelogEntry[];
}

export const useChangelog = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  return useQuery({
    queryKey: ['changelog', currentLanguage],
    queryFn: async (): Promise<VersionWithEntries[]> => {
      const { data: versions, error } = await supabase
        .from('versions')
        .select(`
          *,
          changelog_entries (
            *
          )
        `)
        .eq('is_published', true)
        .order('release_date', { ascending: false });

      if (error) {
        throw error;
      }

      // Transform data to use appropriate language fields
      const transformedVersions = versions?.map(version => ({
        ...version,
        release_notes: currentLanguage === 'de' && version.release_notes_de 
          ? version.release_notes_de 
          : version.release_notes,
        changelog_entries: version.changelog_entries.map(entry => ({
          ...entry,
          title: currentLanguage === 'de' && entry.title_de 
            ? entry.title_de 
            : entry.title,
          description: currentLanguage === 'de' && entry.description_de 
            ? entry.description_de 
            : entry.description,
        }))
      })) || [];

      return transformedVersions as VersionWithEntries[];
    }
  });
};